import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { OPEN_EVENT, clearTrackingCookies, denyConsent, getConsent, grantConsent } from "../lib/consent";

// Asks before Google Analytics / Meta Pixel load (see public/index.html).
// Shown until the visitor picks, and again from the footer's "Cookie settings".
export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Never bake the banner into prerendered pages - it would flash for
    // visitors who have already chosen.
    if (window.__PRERENDER__) return undefined;
    const consent = getConsent();
    // Without consent no tracking cookie should be left behind - covers a
    // just-withdrawn choice and cookies set before this banner existed.
    if (consent !== "granted") clearTrackingCookies();
    if (!consent) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  if (!open) return null;

  const choose = (fn) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent"
      className="fixed inset-x-4 z-[60] rounded-2xl border border-[#E6E9F1] bg-white p-5 shadow-[0_12px_40px_rgba(11,21,51,0.18)] sm:left-5 sm:right-auto sm:max-w-sm"
      style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <p className="text-sm font-semibold text-[#0B1533]">Can we use analytics cookies?</p>
      <p className="mt-1.5 text-sm leading-relaxed text-[#475569]">
        We'd like to use Google Analytics and Meta Pixel to see how visitors use this site and to
        measure our ads. They load only if you accept, and you can change your mind any time from
        "Cookie settings" in the footer.{" "}
        <Link to="/privacy-policy" className="font-semibold text-[#FF6B00] hover:underline">
          Privacy Policy
        </Link>
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={choose(denyConsent)}
          data-testid="cookie-consent-reject"
          className="h-10 rounded-lg border border-[#E6E9F1] text-sm font-semibold text-[#0B1533] transition-colors hover:bg-[#F4F6FB]"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={choose(grantConsent)}
          data-testid="cookie-consent-accept"
          className="h-10 rounded-lg bg-[#FF6B00] text-sm font-semibold text-white transition-colors hover:bg-[#FF8533]"
        >
          Accept
        </button>
      </div>
    </div>
  );
}
